<script setup lang="ts">
import {computed, h, inject} from "vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIconUtils from "@/utils/common/NamaIconUtils.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";
import ActionHandler from "@/utils/moreMenu/ActionHandler.ts";
import {presenterKey} from "@/utils/presenters/interfaces.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import ListViewPerformer from "@/utils/presenters/ListViewPerformer.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import DTOTimelineData = namaTypes.DTOTimelineData;
import DTOTabularResult = namaTypes.DTOTabularResult;
import GUIActionInfo = namaTypes.GUIActionInfo;

const props = defineProps<{
  action: DTOTimelineData,
  isFirstElementInArr: boolean,
  isLastElementInArr: boolean
  isRowDirectionLtr: boolean,
  canShowHorizontalLine: boolean,
  canShowVerticalLine: boolean,
  isHorizontal?: boolean,
  isColumnDirectionTopToDown?: boolean,
  supportSnakeVerticalLayout?: boolean,
  canShowLines?: boolean
}>()

const isVertical = computed(() => !props.isHorizontal)
const isGUIAction = ObjectChecker.isNotEmptyOrNull(props.action.iconAction);
const presenter = inject(presenterKey, null);

const lineHorizontalStyle = computed(() => {
  if ((props.isFirstElementInArr))
    return 'tw-left-1/2'
  else if (props.isLastElementInArr && !props.isRowDirectionLtr)
    return 'tw-left-1/2 !tw-w-1/2'
  else if (props.isLastElementInArr)
    return 'tw-right-1/2 !tw-w-1/2'
  else if (props.isRowDirectionLtr)
    return 'tw-left-0';
  else if (props.isRowDirectionLtr == false)
    return 'tw-right-0';
})
const lineVerticalStyle = computed(() => {
  if ((props.isFirstElementInArr))
    return 'tw-h-1/2 tw-top-1/2'
  else if (props.isLastElementInArr && !props.supportSnakeVerticalLayout)
    return 'tw-top-0 !tw-h-1/2'
  else if (props.isLastElementInArr && !props.isColumnDirectionTopToDown)
    return 'tw-top-1/2 !tw-h-1/2'
  else if (props.isLastElementInArr)
    return 'tw-top-0 !tw-h-1/2'
  else
    return 'tw-top-0'
})

const namaTimelineIconSize = 48;

function getIconActionTitle() {
  const id = props.action.iconAction?.id;
  if (ObjectChecker.isEmptyOrNull(id))
    return '';
  return Translator.translateId(id);
}

async function onIconClick() {
  if (!isGUIAction || !presenter || !(presenter instanceof ListPresenter))
    return;
  const values = (<DTOTabularResult>props.action.row)?.values;
  const performerForPresenter = new ListViewPerformer(presenter);
  const externalContextFields: Record<string, any> = {};
  const metaData = presenter.fetchMetadata();
  const actionInfo: GUIActionInfo = metaData.entityActions.actions.find(a => a.id === props.action.iconAction.id);
  if (actionInfo) {
    const contextFieldIds = actionInfo.contextFields;
    for (let contextFieldId of contextFieldIds) {
      if (contextFieldId in values)
        externalContextFields[contextFieldId] = values[contextFieldId];
    }
  }
  let id: string;
  const runCustomActionOn = props.action.iconAction.customization.runCustomActionOn;
  if (ObjectChecker.isNotEmptyOrNull(runCustomActionOn))
    id = values?.[runCustomActionOn]?.[CommonFieldIds.ID]
  else
    id = values?.[CommonFieldIds.ID]
  performerForPresenter.getSearchContextProvider().externalContextFields = {
    ...values,
    id
  };
  const action = new ActionHandler(
    props.action.iconAction,
    presenter.fetchMetadata(),
    presenter.entityType,
    performerForPresenter,
  );
  await action.performAction();
}

function lineStyleInHorizontalMode() {
  return {transform: `translateY(${namaTimelineIconSize / 2}px)`}
}

function renderAsText(text: string) {
  const props = {class: "dark:tw-text-white/70 tw-text-black/70", innerHTML: text};
  return h("div", props);
}

function renderDisplayedText(en: any, ar: any) {
  if (ObjectChecker.isObject(en) || ObjectChecker.isObject(ar))
    return renderAsRef(ObjectChecker.getFirstNotEmptyObj(en, ar));
  else
    return renderAsText(Translator.calcByLanguage(ar, en))
}

function renderAsRef(ref: Record<string, any>) {
  const text = NamaCommon.nameOfRef(ref);
  const props = {href: PlaceUtils.linkToObject(ref), class: "tw-text-primary-light dark:tw-text-primary-dark tw-underline", title: text};
  return h("a", props, text)
}

function getBoxOrigin(): string {
  let origin: string = "tw-origin-left";
  if (props.isRowDirectionLtr)
    origin = "tw-origin-left";
  else if (props.isColumnDirectionTopToDown)
    origin = "tw-origin-top";
  else if (props.isRowDirectionLtr == false)
    origin = "tw-origin-right";
  else if (props.isColumnDirectionTopToDown == false)
    origin = "tw-origin-bottom";
  return origin;
}
</script>

<template>
  <div
    data-animate-timeline-box
    :class="[{'tw-flex-col':isHorizontal},{'tw-w-fit':(!supportSnakeVerticalLayout && isVertical)},getBoxOrigin()]"
    class="tw-relative tw-h-full tw-gap-1 tw-flex tw-items-center tw-px-2 tw-pt-6 tw-pb-8">
    <!--  header  -->
    <div
      data-animate-from-down
      style="left:50%"
      class="tw-absolute tw-w-full three-dots tw-text-center tw-px-2 tw-top-1 -tw-translate-x-1/2 tw-font-medium"
      v-if="!(!supportSnakeVerticalLayout && isVertical)">
      <component
        :class="[Boolean(action.link?.length)?'!tw-text-primary-light dark:!tw-text-primary-dark tw-underline':'dark:tw-text-gray-400 tw-w-fit tw-max-w-full tw-text-sm tw-text-gray-500 tw-font-semibold']"
        class="tw-mx-auto"
        :is="renderDisplayedText(action.headerEn,action.headerAr)"/>
      <a v-if="action.link" :href="action.link" tabindex="-1" aria-label="Revenue" class="focus:tw-outline-none tw-inset-0">
        <span class="tw-absolute tw-inset-0"
              aria-hidden="true"></span></a>
    </div>
    <!--  IconShape  -->
    <div data-animate-timeline-icon
         class="tw-rounded-full tw-z-10 tw-shrink-0 tw-size-[var(--nama-timeline-icon-size)] tw-flex tw-items-center tw-justify-center active-white"
         :style="`--nama-timeline-icon-size:${namaTimelineIconSize}px`">
      <div
        @click="onIconClick"
        :title="getIconActionTitle()"
        :class="[isGUIAction?'hover:dark:tw-bg-white/10 hover:tw-bg-gray-400 tw-cursor-pointer tw-bg-gray-200':'tw-bg-gray-400']"
        class="tw-relative tw-z-10 tw-p-1 dark:tw-bg-namaDarkGray  tw-rounded-full tw-border-2
        dark:tw-border-[var(--nama-background-bright-dark)] tw-border-[var(--nama-background-bright-light)]">
        <NamaIcon :timeline="action" v-slot="{getNamaIconComponent}">
          <div :style="{backgroundColor:NamaIconUtils.toColorCode({timeline:action})}"
               class="tw-aspect-square tw-rounded-full tw-p-1 tw-flex tw-items-center tw-justify-center">
            <component :is="getNamaIconComponent()" class="!tw-text-[24px] !tw-size-6"/>
          </div>
        </NamaIcon>
      </div>
    </div>
    <div data-animate-from-up class="tw-max-w-96 tw-w-full tw-flex tw-flex-col" :class="{'tw-break-all tw-items-center':isHorizontal}">
      <!--   Header   -->
      <div class="tw-w-fit tw-max-w-full tw-relative"
           v-if="!supportSnakeVerticalLayout && isVertical">
        <component
          :is="renderDisplayedText(action.headerEn,action.headerAr)"
          style="left:50%"
          :class="[Boolean(action.link?.length)?'!tw-text-primary-light dark:!tw-text-primary-dark tw-underline':'dark:tw-text-gray-400 tw-w-fit tw-max-w-full tw-text-sm tw-text-gray-500 tw-font-semibold']"/>
        <a v-if="action.link" :href="action.link" tabindex="-1" aria-label="Revenue" class="focus:tw-outline-none">
        <span class="tw-absolute tw-inset-[0]"
              aria-hidden="true"></span></a>
      </div>
      <!--  date  -->
      <div
        :class="{'tw-break-all':isHorizontal}"
        v-text="DateFieldUtils.dateAndTimeToString(action.date)" dir="ltr"
        class="dark:tw-text-gray-400 tw-text-xs tw-w-fit tw-max-w-full tw-text-gray-500"/>
      <!--   actionType   -->
      <div
        :class="{'tw-break-all':isHorizontal}"
        data-animate-text
        v-html="Translator.translateId(action.actionType)"
        class="dark:tw-text-white/80 tw-px-2 tw-text-sm tw-max-w-full  tw-w-fit tw-text-black/70 tw-font-semibold three-dots"/>
      <!--   subtitle   -->
      <component
        :class="{'tw-break-all tw-text-center':isHorizontal}"
        class="tw-max-w-full three-dots"
        :is="renderDisplayedText(action.subtitleEn,action.subtitleAr)"
      />
    </div>
    <template v-if="canShowLines">
      <!--title="horizontalLine in horizontalMode"-->
    <span
      v-if="isHorizontal"
      data-animate-timline-horizontal-line
      :style="lineStyleInHorizontalMode()"
      :class="[lineHorizontalStyle,getBoxOrigin()]"
      class="tw-absolute tw-h-[1px] tw-w-full tw-bg-gray-400 dark:tw-bg-gray-500"
    >
    </span>
      <!--title="verticalLine in verticalMode"-->
    <span
      v-else
      :class="lineVerticalStyle"
      style="transform: translateX(calc(var(--nama-timeline-icon-size)/2))"
      class="tw-absolute tw-w-[1px] tw-h-full tw-bg-gray-400 dark:tw-bg-gray-500">
    </span>
      <!--title="verticalLine in horizontalMode"-->
    <span
      data-animate-timeline-verticalline
      v-if="canShowHorizontalLine && !isLastElementInArr"
      :style="lineStyleInHorizontalMode()"
      :class="[isRowDirectionLtr?'tw-right-0':'tw-left-0']"
      class="tw-absolute tw-w-[1px] tw-h-full tw-bg-gray-400 dark:tw-bg-gray-500"/>
      <!--title="horizontalLine in verticalMode"-->
    <span
      data-animate-timline-horizontal-line
      v-else-if="canShowVerticalLine && !isLastElementInArr"
      style="transform: translateX(calc(var(--nama-timeline-icon-size)/2))"
      :style="[isColumnDirectionTopToDown?{bottom:0}:{top:0}]"
      class="tw-absolute tw-w-full tw-h-[1px] tw-bg-gray-400 dark:tw-bg-gray-500"/>
    </template>
  </div>
</template>

<style scoped>
[dir="rtl"] .tw-right-1\/2 {
  left: 50% !important;
  right: auto !important;
}

[dir="rtl"] .tw-left-1\/2 {
  right: 50% !important;
  left: auto !important;
}
[dir="rtl"] .tw-right-0 {
  left: 0 !important;
  right: auto !important;
}

[dir="rtl"] .tw-left-0 {
  right: 0 !important;
  left: auto !important;
}
</style>
